/**
 * Country picker for the phone fields (register, phone check).
 *
 * The widget never builds the number itself: it sends what the person typed plus the
 * picked country as `phone_country`, and the server parses the pair with libphonenumber
 * (App\Support\Phone) - "+40712345678", "0040712345678", "0712345678" and "712345678"
 * all land as the same E.164. A number typed with its own prefix ("+36...") ignores the
 * picker. The picker's job is only the prefix-less case, where it outranks the salon's
 * country (a Hungarian client on a Romanian salon's page).
 *
 * List = Shared\Geo\Country::NAMES (RO + HU first, then EU-27 + UK) plus Moldova. Names
 * are English on purpose: the flag and the dial code are what people read, and this
 * keeps the bundle free of a translations dependency for 29 strings.
 *
 * Flags come from one sprite, not emoji (Windows has no flag glyphs: Chrome there shows
 * "RO"): World-Flag-Sprites shiny/24 (MIT), served from the booking host at
 * /images/flags/shiny24/flags.png - 24x24 cells in a 384x384 PNG. Only the PICKED
 * country's flag is shown, laid over the select; an <option> cannot hold an image.
 *
 * `pos` is the cell's background-position, copied from the sprite's own flags.css
 * (.flag.flag-<iso>). That stylesheet is NOT loaded here on purpose: the widget lives in
 * the customer's page, and its global `.flag { width; height; background }` rule would
 * restyle any element of theirs that happens to carry that class. A NEW country below
 * needs its `pos` from public_booking/public/images/flags/shiny24/flags.css.
 */
import {bwBaseOrigin} from './baseUrl.js';

const spriteUrl = () => `${bwBaseOrigin()}/images/flags/shiny24/flags.png`;

export const PHONE_COUNTRIES = [
    { iso: 'ro', dial: '40',  name: 'Romania',        pos: '-168px -264px' },
    { iso: 'hu', dial: '36',  name: 'Hungary',        pos: '0 -144px' },
    { iso: 'md', dial: '373', name: 'Moldova',        pos: '-168px -192px' },
    { iso: 'at', dial: '43',  name: 'Austria',        pos: '-288px 0' },
    { iso: 'be', dial: '32',  name: 'Belgium',        pos: '-96px -24px' },
    { iso: 'bg', dial: '359', name: 'Bulgaria',       pos: '-144px -24px' },
    { iso: 'hr', dial: '385', name: 'Croatia',        pos: '-336px -120px' },
    { iso: 'cy', dial: '357', name: 'Cyprus',         pos: '-120px -72px' },
    { iso: 'cz', dial: '420', name: 'Czechia',        pos: '-144px -72px' },
    { iso: 'dk', dial: '45',  name: 'Denmark',        pos: '-216px -72px' },
    { iso: 'ee', dial: '372', name: 'Estonia',        pos: '-336px -72px' },
    { iso: 'fi', dial: '358', name: 'Finland',        pos: '-120px -96px' },
    { iso: 'fr', dial: '33',  name: 'France',         pos: '-240px -96px' },
    { iso: 'de', dial: '49',  name: 'Germany',        pos: '-168px -72px' },
    { iso: 'gr', dial: '30',  name: 'Greece',         pos: '-144px -120px' },
    { iso: 'ie', dial: '353', name: 'Ireland',        pos: '-72px -144px' },
    { iso: 'it', dial: '39',  name: 'Italy',          pos: '-240px -144px' },
    { iso: 'lv', dial: '371', name: 'Latvia',         pos: '-72px -192px' },
    { iso: 'lt', dial: '370', name: 'Lithuania',      pos: '-24px -192px' },
    { iso: 'lu', dial: '352', name: 'Luxembourg',     pos: '-48px -192px' },
    { iso: 'mt', dial: '356', name: 'Malta',          pos: '-120px -216px' },
    { iso: 'nl', dial: '31',  name: 'Netherlands',    pos: '-48px -240px' },
    { iso: 'pl', dial: '48',  name: 'Poland',         pos: '-360px -240px' },
    { iso: 'pt', dial: '351', name: 'Portugal',       pos: '-72px -264px' },
    { iso: 'sk', dial: '421', name: 'Slovakia',       pos: '-72px -288px' },
    { iso: 'si', dial: '386', name: 'Slovenia',       pos: '-48px -288px' },
    { iso: 'es', dial: '34',  name: 'Spain',          pos: '-48px -96px' },
    { iso: 'se', dial: '46',  name: 'Sweden',         pos: '-360px -264px' },
    { iso: 'gb', dial: '44',  name: 'United Kingdom', pos: '-288px -96px' },
];

export function countryByIso(iso) {
    const code = String(iso || '').toLowerCase();
    return PHONE_COUNTRIES.find(c => c.iso === code) || null;
}

/** ISO of the longest dial code that prefixes an international number ("+36...", "0036..."), else null. */
export function countryByDial(phone) {
    const raw = String(phone || '').replace(/[^\d+]/g, '');
    let digits = null;
    if (raw.startsWith('+')) digits = raw.slice(1);
    else if (raw.startsWith('00')) digits = raw.slice(2);
    if (!digits) return null;
    let best = null;
    for (const c of PHONE_COUNTRIES) {
        if (digits.startsWith(c.dial) && (!best || c.dial.length > best.dial.length)) best = c;
    }
    return best ? best.iso : null;
}

/**
 * Which country to pre-select: what was picked earlier in this widget, else the prefix of a
 * number already known (a logged-in user's E.164), else what the server resolved for the page
 * (account country / request country / language), else RO.
 */
export function pickDefaultCountry(data, sharedState) {
    const st = sharedState || {};
    return (st.phoneCountry && countryByIso(st.phoneCountry) && st.phoneCountry)
        || countryByDial(st.phone)
        || (data && data.phoneCountry && countryByIso(data.phoneCountry) && data.phoneCountry)
        || 'ro';
}

/** The picker for a phone row: flag cell + <select name="phone_country">. Wire it with bindCountryPicker(). */
export function renderCountrySelect({ id, selected }) {
    const picked = countryByIso(selected) || countryByIso('ro');
    const options = PHONE_COUNTRIES.map(c =>
        `<option value="${c.iso}"${c.iso === picked.iso ? ' selected' : ''}>+${c.dial} ${c.name}</option>`
    ).join('');
    return `<span class="phone-country-wrap">`
        + `<span class="phone-flag" aria-hidden="true" style="background-image:url('${spriteUrl()}');background-position:${picked.pos};"></span>`
        + `<select class="phone-country" name="phone_country" id="${id}" aria-label="Country">${options}</select>`
        + `</span>`;
}

/**
 * Wire a rendered picker:
 *  - the flag follows the select;
 *  - a sprite that cannot be loaded (blocked by the host page) drops the flag and gives the
 *    select its padding back - a CSS background has no error event, so the file is probed
 *    once with an Image (the browser serves the background from the same cached response);
 *  - the select follows a number typed WITH its prefix: "+36 30..." flips the picker to HU, so
 *    what the person sees is what the server will do (the prefix wins there anyway).
 */
export function bindCountryPicker($input, $select) {
    const $wrap = $select.closest('.phone-country-wrap');
    const $flag = $wrap.find('.phone-flag');

    const probe = new Image();
    probe.onerror = () => $wrap.addClass('no-flag');
    probe.src = spriteUrl();

    $select.on('change', function () {
        const c = countryByIso($select.val());
        if (c) $flag.css('background-position', c.pos);
    });

    $input.on('input', function () {
        const iso = countryByDial($input.val());
        if (iso && $select.val() !== iso) $select.val(iso).trigger('change');
    });
}
